import { useEffect, useState } from "react";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import { FaPlusSquare } from "react-icons/fa";
import { toast } from "react-toastify";
import { IoTrashOutline } from "react-icons/io5";
import { CiEdit } from "react-icons/ci";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { RxDragHandleDots1 } from "react-icons/rx";
import TestTypeModal from "@modals/TestTypeModal";
import SectionEditModal from "@modals/SectionEditModal";
import SectionPickerModal from "@modals/SectionPickerModal";
import RequestFormFieldsTable from "../elements/tables/RequestFormFieldsTable";

export default function AdminTestTypesTab(props: any) {
  if (!props || !props.tabData) {
    console.error('AdminTestTypesTab: props or props.tabData is undefined');
    return null;
  }

  const { updateSpinner, activeTab, adminPerms } = props.tabData;
  const [testTypeData, updateTestTypeData] = useState<any>({});
  const [editTestType, updateEditTestType] = useState(null);
  const [showTestTypeModal, setShowTestTypeModal] = useState(false);
  const [selectedTestTypeId, updateSelectedTestTypeId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [showSectionEditModal, setShowSectionEditModal] = useState(false);
  const [showSectionPickerModal, setShowSectionPickerModal] = useState(false);
  const [showAllTestTypes, setShowAllTestTypes] = useState(false);
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);

  useEffect(() => {
    if (isOnThisTab()) getTestTypesAsync();
  }, [activeTab]);

  if (!isOnThisTab()) return null;

  function isOnThisTab() {
    return props && activeTab && activeTab === "testTypes";
  }

  function isActiveTestType(testType) {
    if (typeof testType?.active === "boolean") return testType.active;
    return true;
  }

  async function toggleActiveAsync(e) {
    e.stopPropagation();
    const testTypeId = e.currentTarget.parentElement.dataset.id;
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync(`/TestType/${testTypeId}/ActiveToggle`);
    updateSpinner(false);
    if (resp && resp.ok) {
      await getTestTypesAsync();
      toast.success("Test Type Updated");
    }
  }

  async function setRestartModeAsync(testTypeId, mode) {
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync(`/TestType/${testTypeId}/RestartableToggle`, { restartable: mode });
    updateSpinner(false);
    if (resp && resp.ok) {
      await getTestTypesAsync();
      toast.success("Test Type Updated");
    }
  }


  async function getTestTypesAsync() {
    try {
      if (props) {
        updateSpinner(true);

        const resp = await RightTimeApi.getAsync("/TestTypes");
        updateSpinner(false);
        if (resp && resp.ok)
          updateTestTypeData(resp.jsonData);
        else
          console.error("Failed to fetch test types:", resp.statusText);
      }
    } catch (error) {
      console.error("Error fetching test types:", error);
    }
  }


  if ((testTypeData?.testTypes?.length ?? 0) <= 0) return null;

  function getEvalTypeName(value) {
    return Enums.getTestEvalComparisonLabel(value);
  }

  function getCompaniesUsingCount(testType) {
    return testType?.companiesUsing?.length ?? 0;
  }

  function getSectionId(section) {
    return Number(section?.sectionId || section?.id || 0) || 0;
  }

  function SelectedTestType() {
    if (selectedTestTypeId > 0 && testTypeData && testTypeData.testTypes)
      return testTypeData.testTypes.find((tt) => tt.id === selectedTestTypeId);
    return {};
  }
  const selectedTestType = SelectedTestType();

  async function deleteTestTypeSectionAsync(e) {
    let sectionId = parseInt(e.currentTarget.getAttribute("data-id"));
    if (selectedTestTypeId) {
      updateSpinner(true);
      var resp = await RightTimeApi.deleteAsync("/TestType/" + selectedTestTypeId + "/Section/" + sectionId);
      updateSpinner(false);
      if (resp && resp.ok) {
        await getTestTypesAsync();
      }
    }
  }

  async function moveSectionAsync(sectionId, direction) {
    if (selectedTestTypeId) {
      updateSpinner(true);
      await RightTimeApi.putAsync(`/TestType/${selectedTestTypeId}/Section/${sectionId}/Move/${direction}`);
      updateSpinner(false);
      await getTestTypesAsync();
    }
  }

  async function deleteSection(section) {
    if (selectedTestTypeId && section && section.id) {
      updateSpinner(true);
      const resp = await RightTimeApi.deleteAsync(`/TestType/${selectedTestTypeId}/Section/${section.id}`);
      updateSpinner(false);
      if (resp && resp.ok) {
        await getTestTypesAsync();
      }
    }
  }

  async function moveSectionAfter(sectionId, afterSectionId) {
    if (selectedTestTypeId) {
      updateSpinner(true);
      const resp = await RightTimeApi.putAsync(`/TestType/${selectedTestTypeId}/Section/${sectionId}/MoveAfter/${afterSectionId}`);
      updateSpinner(false);
      if (resp && resp.ok) {
        await getTestTypesAsync();
      }
    }
  }

  function handleDragStart(e, section) {
    setDraggedSection(section);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(getSectionId(section)));
  }

  function handleDragOver(e, section) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSection(section);
  }

  function handleDragLeave(e) {
    // Only clear if we're leaving the table area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverSection(null);
    }
  }

  async function handleDrop(e, targetSection) {
    e.preventDefault();
    setDragOverSection(null);

    const draggedSectionId = getSectionId(draggedSection);
    const targetSectionId = getSectionId(targetSection);

    if (!draggedSection || !targetSection || !draggedSectionId || !targetSectionId || draggedSectionId === targetSectionId) {
      setDraggedSection(null);
      return;
    }

    const orderedSectionIds = Array.isArray(selectedTestType?.sections)
      ? selectedTestType.sections.map((section) => getSectionId(section)).filter((sectionId) => sectionId > 0)
      : [];

    const draggedIndex = orderedSectionIds.indexOf(draggedSectionId);
    const targetIndex = orderedSectionIds.indexOf(targetSectionId);

    if (draggedIndex < 0 || targetIndex < 0) {
      setDraggedSection(null);
      return;
    }

    // The borderTop indicator means "insert before this row", so always place after the section preceding the target
    const beforeTargetIndex = targetIndex - 1;
    const afterSectionId = beforeTargetIndex >= 0 ? orderedSectionIds[beforeTargetIndex] : 0;

    await moveSectionAfter(draggedSectionId, afterSectionId);
    setDraggedSection(null);
  }

  function handleDragEnd() {
    setDraggedSection(null);
    setDragOverSection(null);
  }

  function onAddTestTypeAsync(e) {
    e.preventDefault();
    updateEditTestType({ id: 0, name: "" });
    setShowTestTypeModal(true);
  }

  function onAddSection(e) {
    e.stopPropagation();
    setShowSectionPickerModal(true);
  }

  function onSelectTestType(e) {
    console.log("onSelectTestType", e);
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    if (selectedTestTypeId === id) {
      // If already selected, open the edit modal
      let tt = testTypeData.testTypes.find((tt) => tt.id === id);
      if (tt) {
        updateEditTestType(tt);
        setShowTestTypeModal(true);
      }
    } else {
      updateSelectedTestTypeId(id);
    }
  }

  function onStartEditTestType(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    let tt = testTypeData.testTypes.find((tt) => tt.id === id);
    if (tt) {
      updateEditTestType(tt);
      setShowTestTypeModal(true);
    }
  }


  function handleTestTypeModalClose(saved) {
    setShowTestTypeModal(false);
    updateEditTestType(null);
    if (saved) getTestTypesAsync();
  }

  async function handleTestTypeDuplicated(newTestTypeId) {
    updateSelectedTestTypeId(newTestTypeId);
  }

  function handleTestTypeDeleted() {
    updateSelectedTestTypeId(null);
  }

  function onOpenSectionEditModal(section) {
    const sectionContentId = Number(section?.sectionContentId || 0) || null;
    if (!sectionContentId) {
      toast.error("This section has no default content. Please configure it first.");
      return;
    }

    setSelectedSectionId(sectionContentId);
    setShowSectionEditModal(true);
  }

  function onCloseSectionEditModal() {
    if (typeof updateSpinner === "function") updateSpinner(false);
    setShowSectionEditModal(false);
    setSelectedSectionId(null);
  }

  function renderSection(section, i) {
    const sectionId = getSectionId(section);
    const masterSection = testTypeData.sections.find((item) => getSectionId(item) === sectionId);
    const sec = { ...masterSection, ...section };
    if (!sectionId) return null;

    const isDraggedOver = getSectionId(dragOverSection) === sectionId;
    const isDragging = getSectionId(draggedSection) === sectionId;

    // Always render the section name as plain text, no inline editing
    return (
      <tr
        key={"tt" + sectionId}
        data-id={sectionId}
        onDragOver={(e) => handleDragOver(e, sec)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, sec)}
        style={{
          opacity: isDragging ? 0.5 : 1,
          borderTop: isDraggedOver ? '3px solid #007acc' : 'none'
        }}
      >
        <td
          className="dragHandleCell"
          data-help-id="dragHandle"
          onClick={(e) => e.stopPropagation()}
          draggable
          onDragStart={(e) => handleDragStart(e, sec)}
          onDragEnd={handleDragEnd}
          title="Drag to reorder"
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <RxDragHandleDots1
            className="actionIcon dragHandle"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          />
        </td>
        <td>
          <span>{sec.name}</span>
        </td>
        <td className="actions left">
          <CiEdit
            className="actionIcon"
            onClick={() => onOpenSectionEditModal(sec)}
            style={{ cursor: 'pointer' }}
            title="Edit Section"
          />
          <IoTrashOutline
            className="actionIcon"
            onClick={() => deleteSection(sec)}
            style={{ cursor: 'pointer' }}
          />
        </td>
      </tr>
    );
  }

  async function onSelectSectionToAdd(section) {
    const sectionId = Number(section?.sectionId || section?.id || 0);
    if (!selectedTestTypeId || !sectionId) return false;

    updateSpinner(true);
    try {
      const resp = await RightTimeApi.postAsync("/TestTypeSection", { sectionId, testTypeId: selectedTestTypeId });
      if (!resp?.ok) {
        toast.error("Failed to add section.");
        return false;
      }

      await getTestTypesAsync();
      toast.success("Section added.");
      return true;
    } catch {
      toast.error("Error adding section.");
      return false;
    } finally {
      updateSpinner(false);
    }
  }

  if (testTypeData && testTypeData.testTypes && testTypeData.testTypes.length > 0) {
    return (<>
      <div className="adminTestTypesLayout">
        <div className="adminTestTypesColumn adminTestTypesColumnMaster">
          <div className="tableWrapper dimensionTable horizontal masterTestTypeTable">
            <table className="horizontal">
              <caption>
                <span className="title">Master Test Type List</span>
                <span className="captionRight" onClick={onAddTestTypeAsync}>
                  <FaPlusSquare className="actionIcon" />
                </span>
              </caption>
              <thead>
                <tr>
                  <th>Id</th>
                  <th
                    className="verticalHeader"
                    onClick={() => setShowAllTestTypes((prev) => !prev)}
                    data-help-id="ShowSelectedOrAll"
                    style={{ cursor: "pointer" }}
                  >
                    <span>{showAllTestTypes ? "All" : "Active"}</span>
                  </th>
                  <th className="verticalHeader" data-help-id="RestartableTestType"><span>Restart</span></th>
                  <th style={{ minWidth: "200px" }}>Name</th>
                  <th>EvalType</th>
                  <th>Companies</th>
                </tr>
              </thead>
              <tbody>
                {testTypeData && testTypeData.testTypes && testTypeData.testTypes.filter((t) => showAllTestTypes || isActiveTestType(t)).map((t) => {
                  return (
                    <tr
                      key={t.id}
                      className={selectedTestTypeId === t.id ? "active" : ""}
                      data-id={t.id}
                      onClick={onSelectTestType}
                    >
                      <td>{t.id}</td>
                      <td onClick={(e) => { e.stopPropagation(); toggleActiveAsync(e); }} style={{ cursor: 'pointer' }}>
                        {isActiveTestType(t) ? (
                          <MdCheckBox className="actionIcon" />
                        ) : (
                          <MdCheckBoxOutlineBlank className="actionIcon" />
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()} title="Template default copied to new company test types. Does not change existing ones.">
                        <select
                          aria-label="Restart mode"
                          value={Number(t.restartable) || 0}
                          onChange={(e) => setRestartModeAsync(t.id, Number(e.target.value))}
                        >
                          <option value={Enums.TEST_RESTART_MODE.NotRestartable}>Not restartable</option>
                          <option value={Enums.TEST_RESTART_MODE.SectionRestart}>Section restart</option>
                          <option value={Enums.TEST_RESTART_MODE.AnswerRestart}>Answer restart</option>
                        </select>
                      </td>
                      <td className={t.canDelete === true ? "testTypeNameInUse" : undefined}>{t.name}</td>
                      <td>{getEvalTypeName(t.evalComparison)}</td>
                      <td>{getCompaniesUsingCount(t)}</td>
                    </tr>
                  );
                })}

              </tbody>
            </table>
          </div>
        </div>
        {selectedTestType && selectedTestTypeId && selectedTestTypeId >= 0 &&
          (
            <>
              <div className="adminTestTypesColumn adminTestTypesColumnDetails">
                <div className="tableWrapper factTable horizontal TestTypeSectionsTable">
                  <table>
                    <caption>
                      <span className="title">Sections</span>
                      <span className="captionRight">
                          <FaPlusSquare className="actionIcon plusIcon addSection" onClick={onAddSection} />
                      </span>
                    </caption>
                    <thead>
                      <tr>
                        <th></th>
                        <th style={{ minWidth: "170px" }}>Name</th>
                        <th> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTestType && selectedTestType.sections
                        .map((sec, i) => {
                          return renderSection(sec, i);
                        })
                      }
                      {draggedSection && (
                        <tr
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setDragOverSection({ id: 'drop-zone-end' });
                          }}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDragOverSection(null);
                            if (draggedSection) {
                              // Move to end - use the last section's ID as afterSectionId
                              const lastSectionId = getSectionId(selectedTestType.sections[selectedTestType.sections.length - 1]);
                              moveSectionAfter(getSectionId(draggedSection), lastSectionId);
                              setDraggedSection(null);
                            }
                          }}
                          style={{
                            height: '20px',
                            borderTop: dragOverSection && dragOverSection.id === 'drop-zone-end' ? '3px solid #007acc' : 'none'
                          }}
                        >
                          <td colSpan={2} style={{ height: '20px', backgroundColor: '#f0f0f0', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                            Drop here to place at end
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {selectedTestType && (
                  <RequestFormFieldsTable
                    mode="testType"
                    testTypeId={selectedTestTypeId}
                    testTypeFields={selectedTestType.fields ?? []}
                    fieldTypes={testTypeData.fieldTypes ?? []}
                    tabData={props.tabData}
                    updateData={getTestTypesAsync}
                  />
                )}
              </div>
              <div className="adminTestTypesColumn adminTestTypesColumnUsage">
                <div className="tableWrapper factTable horizontal scrollable">
                  <table>
                    <caption>
                      <span className="title">Company Test Types Using This Master Test Type</span>
                    </caption>
                    <thead>
                      <tr>
                        <th>Company Id</th>
                        <th>Company</th>
                        <th>CTT Id</th>
                        <th>CTT Name</th>
                        <th>Tests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedTestType?.companiesUsing?.length ?? 0) > 0 ? (
                        selectedTestType.companiesUsing.map((usage) => (
                          <tr key={`${usage.companyId}-${usage.companyTestTypeId}`}>
                            <td>{usage.companyId}</td>
                            <td>{usage.companyName}</td>
                            <td>{usage.companyTestTypeId}</td>
                            <td>{usage.companyTestTypeName}</td>
                            <td>{usage.testCount}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5}>No companies are currently using this test type.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
      </div>
      <TestTypeModal
        testTypeId={editTestType?.id}
        onClose={handleTestTypeModalClose}
        onDuplicated={handleTestTypeDuplicated}
        onDeleted={handleTestTypeDeleted}
        updateSpinner={updateSpinner}
      />
      <SectionEditModal
        isOpen={showSectionEditModal}
        onClose={onCloseSectionEditModal}
        sectionContentId={selectedSectionId}
        updateSpinner={updateSpinner}
      />
      <SectionPickerModal
        isOpen={showSectionPickerModal}
        onClose={() => setShowSectionPickerModal(false)}
        onSelect={onSelectSectionToAdd}
        excludedSectionIds={selectedTestType?.sections ?? []}
        updateSpinner={updateSpinner}
        title="Add Section"
      />
    </>
    );
  } else {
    return null;
  }
}

